/**
 * BaseGameLoop - drives the frame update cycle and manages the active World.
 * Handles requestAnimationFrame, delta time calculation, and world lifecycle.
 */
import Stats from 'stats.js';
import * as THREE from 'three';
import { InspectorBase, setConsoleFunction } from 'three/webgpu';

import { Pawn } from '../actors/Pawn.js';
import { PlayerController } from '../actors/PlayerController.js';
import { tryApplyLightmapsAfterSceneReady } from '../lightmapper/applySceneLightmaps.js';
import { ClientNetWorld } from '../multiplayer/ClientNetWorld.js';
import { ServerGameSession } from '../multiplayer/implementations/ServerGameSession.js';
import { NetRuntime, NetRuntimeType } from '../multiplayer/NetRuntime.js';
import { NetWorld } from '../multiplayer/NetWorld.js';
import { ServerAuthorityNetWorld } from '../multiplayer/ServerAuthorityNetWorld.js';
import { StandaloneNetWorld } from '../multiplayer/StandaloneNetWorld.js';
import { NavigationEngine } from '../navigation/NavigationServer.js';
import { RecastNavigationServer } from '../navigation/RecastNavigationServer.js';
import { PhysicsEngine } from '../physics/PhysicsEngine.js';
import { Renderer, type RendererOptions } from '../render/Renderer.js';
import { projectInfo } from '../storage/projectContext.js';
import { AssetPath } from '../storage/storageProvider.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { WorldSerializer } from '../systems/WorldSerialization.js';
import { ConnectionStatusOverlay } from '../ui/ConnectionStatusOverlay.js';
import { Delegate } from '../utils/Delegate.js';
import { extractMapNameFromPath } from '../utils/mapNameExtractor.js';
import { resourceManager, ResourceType } from '../utils/ResourceManager.js';
import { spawn } from '../utils/Spawn.js';
import { isNode, parseJson } from '../utils/Utils.js';
import { createVRButton } from '../utils/VRButtonUtils.js';

import {
  applyThreeInspectorTogglePositioning,
  attachFloatingDraggableParametersGroup,
  type InspectorToggleOffset,
  type InspectorTogglePosition,
  nativeRendererHasInspectorSlot,
} from './debugInspectorOptions.js';
import { GameContext, type GameContextConfig } from './GameContext.js';
import { Level } from './Level.js';
import { PlayState } from './Playable.js';
import { World } from './World.js';

import type { WorldOptions } from './World.js';
import type { ILoadingScreen } from '../ui/LoadingScreen.js';
import type { Inspector } from 'three/addons/inspector/Inspector.js';

/** In-browser debug HUD driven by {@link BaseGameLoop}. */
export type DebugUIMode = 'none' | 'stats' | 'inspector';

export type { InspectorTogglePosition, InspectorToggleOffset } from './debugInspectorOptions.js';

export interface IGameLoop {
  loadLevelData(levelData: any): Promise<Level>;
  loadLevelDataFromPath(path: AssetPath): Promise<Level>;

  start(): Promise<void>;
  destroy(): void;
  setInputEnabled(enabled: boolean): void;
  getWorld(): World | null;
  getLevel(): Level | null;
}

export interface BaseGameLoopOptions {
  backgroundColor: THREE.ColorRepresentation;
  gravity: THREE.Vector3;
  gameId?: string;
  xrEnabled?: boolean;
  /** WebXR session options for VR button */
  vrSessionOptions?: {
    optionalFeatures?: string[];
    requiredFeatures?: string[];
    framebufferScaleFactor?: number;
  };
  /** Renderer options (supports both WebGL and WebGPU) */
  rendererOptions?: RendererOptions;

  /**
   * The GameContext class to use when creating a new instance.
   * If not specified, uses the base GameContext class.
   */
  gameContextClass?: new (config: GameContextConfig) => GameContext;

  /**
   * Configuration for creating a GameContext if one isn't provided.
   * A GameContext is always created during initialization; this config customizes it.
   */
  gameContextConfig?: GameContextConfig;

  /**
   * Force headless mode (no rendering/UI) even in non-server environments.
   * Useful for testing or running browser-based simulations without rendering.
   * Defaults to true when NetRuntime.isDedicatedServer() is true or running in Node.js.
   */
  headless?: boolean;

  /**
   * Debug HUD: `none`, legacy `stats.js` FPS plus engine draw-call overlay, or Three.js addon inspector.
   * @defaultValue `'inspector'`
   */
  debugUIMode?: DebugUIMode;

  /** Corner or edge anchor for the Three.js inspector FPS toggle (`#profiler-toggle`) inside the game container. */
  inspectorTogglePosition?: InspectorTogglePosition;
  /** Pixel inset from the anchored edge; how `x`/`y` apply depends on {@link InspectorTogglePosition} (see {@link InspectorToggleOffset}). */
  inspectorToggleOffset?: InspectorToggleOffset;
  /** Uniform CSS scale for the FPS toggle (invalid or non-positive values fall back in layout code). */
  inspectorToggleScale?: number;
}

export interface TickTime {
  currentTimeMS: number;
  deltaTimeMS: number;
}

export class BaseGameLoop implements IGameLoop {
  private _world: World | null = null;
  protected stats: Stats | null = null;
  /** Three.js addon inspector instance when {@link BaseGameLoopOptions.debugUIMode} is `inspector` and the renderer supports it. */
  private threeInspector: Inspector | null = null;
  private disposeProfilerPanel: (() => void) | null = null;
  protected lastTickTimeMS: number = 0;
  private _renderer: Renderer | null = null;
  protected destroyed: boolean = false;
  protected options: BaseGameLoopOptions;
  public readonly onWorldDataLoaded: Delegate<[Level], void> = new Delegate<[Level], void>();
  private vrButton: HTMLElement | null = null;
  protected gameContext: GameContext;

  /** Network world for multiplayer - persists across world transitions */
  protected netWorld: NetWorld;

  static get DEFAULT_OPTIONS(): BaseGameLoopOptions {
    return {
      backgroundColor: 0x87CEEB,
      gravity: new THREE.Vector3(0, -9.81, 0),
      xrEnabled: false,
      debugUIMode: 'inspector',
      inspectorTogglePosition: 'top-left',
      inspectorToggleOffset: { x: 10, y: 10 },
      inspectorToggleScale: 0.8,
    };
  }

  protected level: Level | null = null;
  protected loadingScreen: ILoadingScreen | null = null;
  protected readonly headless: boolean = false;

  get renderer(): Renderer | null {
    return this._renderer;
  }
  private set renderer(value: Renderer | null) {
    this._renderer = value;
  }

  //====================================================================================================

  constructor(protected container: HTMLElement, options: Partial<BaseGameLoopOptions> = {}) {
    ObjectTracker.track(this);
    // clear the cached resources to avoid loading stale resources
    resourceManager.clearCachedResources();

    // Support nested options format where gameId may contain additional options.
    if (options.gameId && typeof options.gameId === 'object') {
      // Extract the nested options and merge with top-level options
      const nestedOptions = options.gameId as any;
      options = {
        ...options,
        ...nestedOptions,
        gameId: nestedOptions.gameId, // Extract the actual gameId string
      };
    }

    // Merge provided options with defaults.
    const defaults = BaseGameLoop.DEFAULT_OPTIONS;
    this.options = {
      ...defaults,
      ...options,
      inspectorToggleOffset: {
        x: options.inspectorToggleOffset?.x ?? defaults.inspectorToggleOffset?.x ?? 10,
        y: options.inspectorToggleOffset?.y ?? defaults.inspectorToggleOffset?.y ?? 10,
      },
    };

    // Create NetWorld early — it persists across world transitions.
    // Dedicated servers use ServerAuthorityNetWorld; all other modes start as StandaloneNetWorld.
    // connectToServer() swaps StandaloneNetWorld → ClientNetWorld on connect and back on disconnect.
    const gameId = this.options.gameId ?? 'default';
    this.netWorld = NetRuntime.isDedicatedServer()
      ? new ServerAuthorityNetWorld(gameId)
      : new StandaloneNetWorld(gameId);

    // Skip renderer and stats in headless mode
    this.headless = options.headless ?? (NetRuntime.isDedicatedServer() || isNode());

    if (!this.headless) {
      this.container.style.position = 'relative';

      const debugUIMode = this.options.debugUIMode ?? 'inspector';
      if (debugUIMode === 'stats') {
        this.stats = new Stats();
        this.stats.dom.style.position = 'absolute';
        this.stats.dom.style.top = '10px';
        this.stats.dom.style.left = '10px';
        this.container.appendChild(this.stats.dom);
        this.stats.showPanel(0);
      }
    }

    // Always create GameContext so startup runs through a single unified path.
    this.gameContext = this.createGameContext(options.gameContextConfig ?? {});
  }

  /**
   * Sets up XR and console after a world has been created.
   * Called after world creation in the unified GameContext startup path.
   */
  protected setupWorldAfterCreation(): void {
    if (!this._world) return;

    // Set up XR if enabled in options
    if (this.options.xrEnabled && this.renderer) {
      this.renderer.enableXR();

      // Set framebuffer scale factor if specified
      if (this.options.vrSessionOptions?.framebufferScaleFactor !== undefined) {
        this.renderer.native.xr.setFramebufferScaleFactor(this.options.vrSessionOptions.framebufferScaleFactor);
        console.log('🎯 XR framebuffer scale factor set to:', this.options.vrSessionOptions.framebufferScaleFactor);
      }
    }

    this._world.consoleManager.addCommandHandler(this);
  }

  /** Effective debug UI mode for this game loop. */
  public getDebugUIMode(): DebugUIMode {
    return this.options.debugUIMode ?? 'inspector';
  }

  /**
   * Active Three.js addon inspector when initialised; use {@link createThreeInspectorParametersGroup} for custom panels.
   */
  public getThreeInspector(): Inspector | null {
    return this.threeInspector;
  }

  /**
   * Shows or hides the Three.js inspector DOM. No-op when the inspector was not initialised.
   */
  public setThreeInspectorVisible(visible: boolean): void {
    if (!this.threeInspector) {
      return;
    }
    const el = this.threeInspector.domElement;
    el.style.display = visible ? '' : 'none';
    el.style.visibility = visible ? 'visible' : 'hidden';
  }

  /**
   * Toggles Three.js inspector visibility (used by `show stats` when debug UI mode is `inspector`).
   */
  public toggleThreeInspectorVisibility(): string {
    if (this.getDebugUIMode() !== 'inspector') {
      return 'Three Inspector is only used when debugUIMode is inspector.';
    }
    if (!this.threeInspector) {
      return 'Three Inspector was not initialised (no .inspector slot on the active native renderer).';
    }
    const el = this.threeInspector.domElement;
    const hidden = el.style.display === 'none' || el.style.visibility === 'hidden';
    this.setThreeInspectorVisible(!hidden);
    return hidden ? 'Three Inspector shown' : 'Three Inspector hidden';
  }

  /**
   * Creates a named parameter group on the Three Inspector (same API as three.js examples).
   * @returns The group handle, or `null` if the inspector is not active.
   */
  public createThreeInspectorParametersGroup(name: string): ReturnType<Inspector['createParameters']> | null {
    return this.threeInspector?.createParameters(name) ?? null;
  }

  /**
   * Attaches the Three.js addon inspector when {@link BaseGameLoopOptions.debugUIMode} is `inspector`.
   * Waits for WebGPU initialisation when applicable.
   */
  protected async setupInspector(): Promise<void> {
    const mode = this.options.debugUIMode ?? 'inspector';
    if (mode !== 'inspector' || !this.renderer) {
      return;
    }
    const genesysRenderer = this.renderer.native;
    const native = genesysRenderer.asWebGL() ?? genesysRenderer.asWebGPU();
    if (!nativeRendererHasInspectorSlot(native)) {
      console.warn(
        '[GameLoop] Three.js addon Inspector requires a native renderer with an .inspector slot (WebGPURenderer, including WebGL fallback). Classic WebGLRenderer does not expose this API.'
      );
      return;
    }
    const { Inspector: ThreeInspector } = await import('three/addons/inspector/Inspector.js');
    this.threeInspector = new ThreeInspector();
    native.inspector = this.threeInspector;
    const el = this.threeInspector.domElement;
    applyThreeInspectorTogglePositioning(el, {
      position: this.options.inspectorTogglePosition ?? BaseGameLoop.DEFAULT_OPTIONS.inspectorTogglePosition ?? 'top-left',
      offset: {
        x: this.options.inspectorToggleOffset?.x ?? BaseGameLoop.DEFAULT_OPTIONS.inspectorToggleOffset?.x ?? 10,
        y: this.options.inspectorToggleOffset?.y ?? BaseGameLoop.DEFAULT_OPTIONS.inspectorToggleOffset?.y ?? 10,
      },
      scale: this.options.inspectorToggleScale ?? BaseGameLoop.DEFAULT_OPTIONS.inspectorToggleScale ?? 0.8,
    });

    this.container.appendChild(el);
    this.setupProfilerPanel();
  }

  private setupProfilerPanel(): void {
    if (!THREE.ProfilerService.isEnabled()) return;
    const gui = this.createThreeInspectorParametersGroup('Profiler');
    if (!gui) return;

    const actions = {
      toggle: () => {
        if (THREE.ProfilerService.isEnabled()) {
          THREE.ProfilerService.downloadTrace();
          THREE.ProfilerService.disable();
          toggleCtrl.name('▶ Start');
        } else {
          THREE.ProfilerService.enable();
          toggleCtrl.name('■ Stop + Download trace');
        }
      },
      report: () => THREE.ProfilerService.report(),
    };

    const toggleCtrl = gui.add(actions, 'toggle').name(THREE.ProfilerService.isEnabled() ? '■ Stop + Download trace' : '▶ Start');
    gui.add(actions, 'report').name('Report → console');

    this.disposeProfilerPanel?.();
    this.disposeProfilerPanel = attachFloatingDraggableParametersGroup(this.container, gui);

    const inspectorWithParameters = this.threeInspector as Inspector & {
      parameters?: { hide(): void };
    };
    inspectorWithParameters.parameters?.hide();
  }

  private disposeThreeInspector(): void {
    this.disposeProfilerPanel?.();
    this.disposeProfilerPanel = null;

    if (!this.threeInspector || !this.renderer) {
      this.threeInspector = null;
      return;
    }
    const inspector = this.threeInspector as any;
    try {
      const genesysRenderer = this.renderer.native;
      const native = genesysRenderer.asWebGL() ?? genesysRenderer.asWebGPU();
      if (nativeRendererHasInspectorSlot(native)) {
        // Detach first so no new inspector frame hooks are scheduled on the live renderer.
        native.inspector = new InspectorBase();
      }

      // Three Inspector may still have queued RAF timestamp work after teardown.
      // Keep those callbacks safe by switching to a stable no-op renderer state without
      // routing through Inspector.setRenderer(), which expects a full renderer contract.
      const safeInspectorRenderer = {
        init: async () => { },
        _nodes: { nodeFrame: { deltaTime: 0, frameId: 0 } },
        resolveTimestampsAsync: async () => { },
        hasFeature: () => false,
        backend: {
          trackTimestamp: false,
          getTimestampFrames: () => [] as number[],
          hasTimestamp: () => false,
          getTimestamp: () => 0,
        },
      };

      const originalSetRenderer = inspector.setRenderer?.bind(inspector);
      const activeRenderer = inspector.getRenderer?.() as {
        resolveTimestampsAsync?: () => Promise<void>;
        backend?: { trackTimestamp?: boolean };
      } | null;
      if (activeRenderer) {
        activeRenderer.resolveTimestampsAsync = async () => { };
        if (activeRenderer.backend && 'trackTimestamp' in activeRenderer.backend) {
          activeRenderer.backend.trackTimestamp = false;
        }
      }
      setConsoleFunction((type: 'log' | 'warn' | 'error', message: string, ...params: unknown[]) => {
        if (type === 'error' && message.includes('THREE.Error resolving queries:')) {
          return;
        }
        if (type === 'warn') {
          console.warn(message, ...params);
          return;
        }
        if (type === 'error') {
          console.error(message, ...params);
          return;
        }
        console.log(message, ...params);
      });
      inspector.resolveTimestamp = async () => { };
      inspector._resolveTimestampPromise = null;
      inspector.addFrame = () => { };
      inspector._renderer = safeInspectorRenderer;
      if (originalSetRenderer) {
        inspector.setRenderer = (renderer: unknown) => {
          if (renderer === null) {
            return inspector;
          }
          if (
            typeof renderer === 'object'
            && renderer !== null
            && typeof (renderer as { init?: unknown }).init === 'function'
          ) {
            originalSetRenderer(renderer);
          }
          return inspector;
        };
      }
    } catch (error) {
      console.warn('[GameLoop] Failed to reset renderer inspector:', error);
    }
    this.threeInspector.domElement.remove();
    this.threeInspector = null;
  }

  /**
   * Gets the current world. Throws if no world exists.
   */
  public get world(): World {
    if (!this._world) {
      throw new Error('[GameLoop] World not initialized. Call start() first when using GameContext.');
    }
    return this._world;
  }

  /**
   * Sets the world. Used by GameContext during startup.
   * @internal
   */
  public setWorld(world: World): void {
    this._world = world;
  }

  /**
   * Creates a GameContext. Override to provide a custom GameContext subclass.
   * Uses gameContextClass from options if provided, otherwise defaults to base GameContext.
   */
  protected createGameContext(config: GameContextConfig): GameContext {
    const GameContextClass = this.options.gameContextClass ?? GameContext;
    return new GameContextClass(config);
  }

  /**
   * Gets the game context used by this loop.
   */
  public getGameContext(): GameContext {
    return this.gameContext;
  }

  /**
   * Get the world configuration. Override this to change the world configuration in your child class.
   * @returns The world configuration.
   */
  public getWorldConfiguration(): WorldOptions {
    const baseOptions = {
      backgroundColor: this.options.backgroundColor,
      physicsOptions: {
        gravity: this.options.gravity,
        engine: PhysicsEngine.Rapier
      },
      navigationOptions: {
        engine: NavigationEngine.RecastNavigation,
        debug: false,
        options: RecastNavigationServer.DEFAULT_OPTIONS,
        generateOnStartUp: false
      },
      useManifold: false,
      enableGamepad: true,
      netWorld: this.netWorld
    };
    const isHeadless = this.options.headless ?? (NetRuntime.isDedicatedServer() || isNode());
    if (isHeadless) {
      return {
        ...baseOptions,
        headless: true
      };
    }
    if (!this.renderer?.native.domElement) {
      throw new Error('[GameLoop] rendererDomElement is required for non-headless world configuration');
    }
    return {
      ...baseOptions,
      headless: false,
      rendererDomElement: this.renderer.native.domElement,
      gameContainer: this.container,
      debugUIMode: this.options.debugUIMode ?? 'inspector',
    };
  }

  public getDefaultRendererOptions(): RendererOptions {
    return {
      webgl: {
        powerPreference: 'high-performance',
        antialias: this.options.xrEnabled, // antialias is set here for XR only as it does not support postprocessing
      },
    };
  }

  public async start(): Promise<void> {
    console.log('[GameLoop] Starting GameContext...');

    if (!this.headless) {
      const defaultOptions = this.getDefaultRendererOptions();
      const rendererOptions: RendererOptions = {
        ...defaultOptions,
        ...this.options.rendererOptions,
        webgl: { ...defaultOptions.webgl, ...this.options.rendererOptions?.webgl },
        webgpu: { ...defaultOptions.webgpu, ...this.options.rendererOptions?.webgpu },
      };
      this.renderer = await Renderer.create(this.container, rendererOptions);
      resourceManager.initWithRenderer(this.renderer);
    }

    this.gameContext.setGameLoop(this);
    await this.gameContext.startGameContext({
      ...this.getWorldConfiguration(),
      gameId: this.options.gameId,
      gameLoop: new WeakRef(this)
    });
    this.setupWorldAfterCreation();
    await this.setupInspector();

    if (!this.world) {
      throw new Error('[GameLoop] World not initialized after start. This should not happen.');
    }
    console.log('[GameLoop] waiting for resources (1)...');
    await resourceManager.waitForResources(ResourceType.All);

    // create navigation server and manifold before loading scene resources
    await this.world.createNavigationServer();
    await this.world.createManifold();

    console.log('[GameLoop] loading scene resources...');
    await this.loadSceneResources(this.world);
    await this.loadSceneLightmaps(this.world);

    if (this.world.options.navigationOptions.generateOnStartUp) {
      await this.world.createNavigationMeshFromScene();
    }

    console.log('[GameLoop] calling preStart...');
    await this.preStart();

    // Auto-create VR button if XR is enabled
    if (this.options.xrEnabled) {
      this.createVRButton().catch(error => {
        console.warn('[GameLoop] failed to create VR button:', error);
      });
    }

    console.log('[GameLoop] calling world.beginPlay...');
    this.world.beginPlay();

    // wait for the local player controller to be spawned
    // the logic in the game loop doesn't really depend on it
    // it's purely a convenience so that the user code can get the player controller immediately gameloop.start() is called
    await this.netWorld.waitForLocalPlayerController();

    // Notify subclasses that the server is ready (dedicated server only, after beginPlay).
    if (NetRuntime.isDedicatedServer()) {
      const session = this.netWorld.getGameSession();
      if (session instanceof ServerGameSession) {
        this.onServerReady(session.getPort());
      }
    }

    // Local function to be used as animation loop callback
    const tickCallback = (currentTimeMS: number) => {
      if (this.destroyed) {
        return;
      }

      if (!this.options.xrEnabled) {
        // For XR, this is handled by three.js itself using WebGlRenderer.setAnimationLoop
        requestAnimationFrame(tickCallback);
      }

      if (this.stats) {
        this.stats.begin();
      }
      // Initialize lastTime on first frame to prevent large initial deltaTime
      if (!this.lastTickTimeMS) {
        this.lastTickTimeMS = currentTimeMS;
        this.stats?.end();
        return;
      }

      const deltaTimeMS = currentTimeMS - this.lastTickTimeMS;
      this.lastTickTimeMS = currentTimeMS;

      this.tick({ currentTimeMS, deltaTimeMS });

      if (this.stats) {
        this.stats.end();
      }
    };

    // Always use WebGL renderer's setAnimationLoop when XR is enabled
    // This is required by Three.js for proper VR support
    const isHeadless = this.options.headless ?? NetRuntime.isDedicatedServer();
    if (this.options.xrEnabled && this.renderer) {
      this.renderer!.setupXRAnimationLoop(tickCallback);
    } else if (!isHeadless) {
      requestAnimationFrame(tickCallback);
    }

    console.log('[GameLoop] waiting for resources (2)...');
    await resourceManager.waitForResources(ResourceType.All);
    console.log('[GameLoop] resources loaded');
    this.stopLoadingScreen();

    console.log('[GameLoop] calling postStart...');
    this.postStart();
  }

  public destroy(): void {
    this.level = null;

    this.disposeThreeInspector();

    // Shutdown GameContext (unified startup path).
    this.gameContext.shutdown();
    if (this._world) {
      this._world.destroy();
    }

    this._world = null;
    this.renderer?.destroy();
    this.stats?.dom.remove();
    this.stats = null;

    // Clean up VR button if it exists
    if (this.vrButton) {
      this.vrButton.remove();
      this.vrButton = null;
    }

    this.destroyed = true;
  }

  public setInputEnabled(enabled: boolean): void {
    this.world.inputManager.setInputEnabled(enabled);
  }

  public async loadLevelData(levelData: any): Promise<Level> {
    const isConnectedToServer = !!this.world.netWorld?.getGameSession();

    if (isConnectedToServer) {
      // When connected to a server, remove ALL actors except dynamically spawned ones from server
      // Dynamic actors (like PlayerController, PlayerInfo) are spawned at runtime by the server
      // Static actors (from scene file) should be removed and reloaded from the new map
      const allActors = [...this.world.getActors()];

      // Preserve only dynamic actors (not static objects from scene files)
      // Dynamic actors have remoteNetClientId set, static actors don't
      const actorsToPreserve = allActors.filter(actor => !actor.isStaticNetObject());
      const actorsToRemove = allActors.filter(actor => actor.isStaticNetObject());

      console.log(`[GameLoop] Connected to server - removing ${actorsToRemove.length} static actors, preserving ${actorsToPreserve.length} dynamic actor(s)`);
      this.world.removeActors(...actorsToRemove);

      // Clear the level reference since we removed everything from it
      this.level = null;
    } else {
      // In standalone mode, just unload the previous level normally
      if (this.level) {
        console.log(`[GameLoop] unloading previous level with ${this.level.actors.length} actors`);
        this.level.unload();
      }
    }

    // Server-side map transition: destroy the current GameMode before loading the new scene
    // so the incoming scene can declare its own GameMode class.
    // Save activePlayers so they can be transferred to the new GameSessionInfo afterwards.
    let savedActivePlayers: Map<string, import('../actors/PlayerInfo.js').PlayerInfo> | null = null;
    if (NetRuntime.isDedicatedServer() && this.world.gameMode) {
      const oldSessionInfo = this.world.gameMode.getGameSessionInfo();
      if (oldSessionInfo) {
        savedActivePlayers = new Map(oldSessionInfo.activePlayers);
        this.world.removeActors(oldSessionInfo);
      }
      if (this.world.gameMode.getPlayState() === PlayState.Playing) {
        this.world.gameMode.endPlay();
      }
      this.world.removeActors(this.world.gameMode);
      (this.world as any).gameMode = null;
      (this.world as any).gameModeClass = null; // let the scene set the new class
      console.log('[GameLoop] Destroyed old GameMode for map transition');
    }

    this.level = await WorldSerializer.loadWorld(this.world, levelData);

    // Server-side: create a fresh GameMode from the class declared by the new scene,
    // then transfer the preserved activePlayers into the new GameSessionInfo.
    if (NetRuntime.isDedicatedServer() && this.world.gameModeClass) {
      const newGameMode = spawn(this.world.gameModeClass);
      (this.world as any).gameMode = newGameMode;
      this.world.addActors(newGameMode); // triggers beginPlay → spawnGameSessionInfo

      if (savedActivePlayers && savedActivePlayers.size > 0) {
        const newSessionInfo = newGameMode.getGameSessionInfo();
        if (newSessionInfo) {
          for (const [clientId, playerInfo] of savedActivePlayers) {
            newSessionInfo.activePlayers.set(clientId, playerInfo);
          }
          console.log(`[GameLoop] Transferred ${savedActivePlayers.size} preserved PlayerInfo entries to new GameSessionInfo`);
        }
      }
    }

    return this.level;
  }

  /**
   * Loads a map by its short name (e.g., 'my-level' resolves to @project/my-level.genesys-scene).
   */
  public async loadMap(mapPath: string): Promise<void> {
    const fullPath = `@project/${mapPath}.genesys-scene`;
    console.log(`[GameLoop] 🗺️ Loading map: "${mapPath}" (path: ${fullPath})`);

    this.onBeforeMapLoad(mapPath);

    try {
      await this.loadLevelDataFromPathInternal(AssetPath.fromString(fullPath));
    } catch (error) {
      console.error('[GameLoop] Failed to load map:', error);
      throw error;
    }

    // Notify NetWorld that the server has finished loading the new scene so deferred
    // client joins can be flushed (ServerAuthorityNetWorld.onServerMapReady()).
    if (NetRuntime.isDedicatedServer()) {
      this.netWorld.onServerMapReady();
    }

    this.onAfterMapLoad(mapPath);
  }

  /** @deprecated Use loadMap instead. */
  public async loadLevelDataFromPath(path: AssetPath): Promise<Level> {
    console.warn('[GameLoop] loadLevelDataFromPath() is deprecated — use loadMap() instead.');
    return this.loadLevelDataFromPathInternal(path);
  }

  private async loadLevelDataFromPathInternal(path: AssetPath): Promise<Level> {
    console.log(`[GameLoop] loading world data from ${path}`);
    const levelData = await resourceManager.loadJsonObject(path);
    console.log(`[GameLoop] world data loaded from ${path}, loading...`);

    // Extract map name from path and set it on NetWorld (server-side only)
    const mapName = extractMapNameFromPath(path);
    if (mapName && this.world.netWorld && NetRuntime.isDedicatedServer()) {
      this.world.netWorld.setMapName(mapName);
    }

    // Load the world data
    const level = await this.loadLevelData(levelData);

    this.gameContext.setActiveScenePath(path);

    // Notify listeners that world data has been loaded
    this.onWorldDataLoaded.invoke(level);

    if (this.world) {
      await this.loadSceneLightmaps(this.world);
    }

    return level;
  }

  /**
   * Loads scene resources (NavMesh) based on scene metadata.
   */
  protected async loadSceneResources(world: World): Promise<void> {
    const resources = world.sceneResources;
    if (!resources) {
      console.log('[GameLoop] No scene resources configured, skipping automatic loading');
      return;
    }

    if (resources.navMesh) {
      await this.loadNavMeshFromPath(resources.navMesh.path);
    }
  }

  /**
   * Applies co-located lightmap build-data for the active scene path.
   */
  protected async loadSceneLightmaps(world: World): Promise<void> {
    const scenePath = this.gameContext.getActiveScenePath();
    if (!scenePath) {
      return;
    }
    await tryApplyLightmapsAfterSceneReady(world, scenePath.initialPath);
  }

  /**
   * Loads NavMesh from an explicit file path.
   */
  protected async loadNavMeshFromPath(navMeshPath: string): Promise<void> {
    if (!this.world) {
      return;
    }
    try {
      const assetPath = new AssetPath(navMeshPath);
      const provider = projectInfo.storageProvider;

      // Load NavMesh data directly
      const navMeshBuffer = await provider.downloadFileAsBuffer(assetPath);
      const navigationServer = this.world.getNavigationServer();

      if (navigationServer) {
        const success = await navigationServer.importNavigationMeshFromData(navMeshBuffer);
        if (success) {
          console.log(`[GameLoop] ✓ NavMesh loaded from: ${navMeshPath}`);
          return;
        }
      }

    } catch (error) {
      console.warn(`[GameLoop] ⚠️ Could not load NavMesh from: ${navMeshPath}`, error);
    }

    // Fallback: Generate NavMesh from scene geometry if auto-generation is enabled
    const navigationOptions = this.world.options.navigationOptions;
    if (navigationOptions.generateOnStartUp) {
      try {
        console.log('[GameLoop] 🔄 Falling back to auto-generation of NavMesh');
        await this.world.createNavigationMeshFromScene();
        console.log('[GameLoop] ✓ NavMesh auto-generated from scene geometry');
      } catch (error) {
        console.error('[GameLoop] ✗ Failed to auto-generate NavMesh', error);
      }
    } else {
      console.log('[GameLoop] ℹ️ NavMesh auto-generation disabled');
    }
  }

  public getWorld(): World | null {
    return this._world;
  }

  public getLevel(): Level | null {
    return this.level;
  }

  public enableXR(): void {
    this.options.xrEnabled = true;
    this.renderer?.enableXR();
  }

  public disableXR(): void {
    this.options.xrEnabled = false;
    this.renderer?.disableXR();
  }

  public async createVRButton(): Promise<HTMLElement | null> {
    try {
      if (!this.renderer) {
        throw new Error('Cannot create VR button in headless mode');
      }

      // Clean up existing VR button if any
      if (this.vrButton) {
        this.vrButton.remove();
        this.vrButton = null;
      }

      // Use utility function with configurable features
      // VR requires WebGL renderer (WebGPU XR is experimental)
      const webglRenderer = this.renderer.native.asWebGL();
      if (webglRenderer) {
        this.vrButton = await createVRButton(
          webglRenderer,
          this.container,
          this.options.vrSessionOptions ?? {}
        );
        return this.vrButton;
      }
      return null;
    } catch (error) {
      console.error('[GameLoop] failed to load VRButton:', error);
      return Promise.reject(error);
    }
  }

  public isXREnabled(): boolean {
    return this.renderer?.isXREnabled() ?? false;
  }

  public isXRPresenting(): boolean {
    return this.renderer?.isXRPresenting() ?? false;
  }

  public async exportNavigationMesh(scenePath: AssetPath): Promise<{ success: boolean; data?: ArrayBuffer; filename?: string }> {
    if (!this.world) {
      return { success: false };
    }
    return await this.world.exportNavigationMesh(scenePath);
  }

  public async importNavigationMesh(navMeshData?: ArrayBuffer): Promise<boolean> {
    if (!this.world) {
      return false;
    }
    if (navMeshData) {
      // Import from provided data
      const navigationServer = this.world.getNavigationServer();
      if (navigationServer) {
        return await navigationServer.importNavigationMeshFromData(navMeshData);
      }
      return false;
    }

    // Import from scene resources configuration
    const navMeshPath = this.world.sceneResources?.navMesh?.path;
    if (navMeshPath) {
      await this.loadNavMeshFromPath(navMeshPath);
      return true;
    }

    console.error('[GameLoop] importNavigationMesh: No NavMesh data provided and no navMesh configured in scene resources');
    return false;
  }

  public startLoadingScreen(customLoadingScreen: ILoadingScreen | null = null): void {
    // Skip loading screen if world doesn't exist yet or on server
    if (!this._world || NetRuntime.isDedicatedServer()) {
      return;
    }

    if (!this.loadingScreen) {
      this.loadingScreen = customLoadingScreen ?? this.createLoadingScreen();
      if (this.loadingScreen) {
        console.trace('[GameLoop] starting loading screen');
        this.loadingScreen.start(this._world);
      }
    }
  }

  public stopLoadingScreen(): void {
    if (this.loadingScreen) {
      console.trace('[GameLoop] stopping loading screen');
      this.loadingScreen.stop();
      this.loadingScreen = null;
    }
  }

  //====================================================================================================

  /**
   * Called after GameContext.startGameContext() and scene-resource loading, but before
   * navigation/physics creation and world.beginPlay(). Override to configure GameMode
   * options, add static actors, or set session configuration.
   */
  protected async preStart(): Promise<void> {
  }

  /**
   * Called after the render loop is registered and world.beginPlay() has completed.
   * Override to spawn player controllers, start timers, or hook UI.
   */
  protected postStart(): void {
  }

  /**
   * Called after the WebSocket server has bound to a port, before any clients connect.
   */
  protected onServerReady(_port: number): void {
    // Override in subclasses to react to the server binding successfully.
  }

  /**
   * Dispatches to connectToServer or loadMap based on arg. Server URLs are detected
   * by ws:// or wss:// prefix. All other strings are treated as map names.
   */
  public async open(arg: string): Promise<void> {
    if (arg.startsWith('ws://') || arg.startsWith('wss://')) {
      await this.connectToServer(arg);
    } else {
      await this.loadMap(arg);
    }
  }

  /**
   * Called by loadMap before destroying existing actors. Override to show a loading screen.
   */
  protected onBeforeMapLoad(_mapName: string): void {
  }

  /**
   * Called by loadMap after the new scene actors are added. Override to hide the loading screen.
   */
  protected onAfterMapLoad(_mapName: string): void {
    // Override to hide the loading screen, re-enable input, etc.
  }

  protected createLoadingScreen(): ILoadingScreen | null {
    return null;
  }

  protected tick(tickTime: TickTime): void {
    if (!this.world) return;

    this.world.tick(tickTime.deltaTimeMS / 1000);

    this.renderer?.render(this.world);
  }

  @ConsoleCommand('profiler.enable', 'Enable CPU profiling — use profiler.report to see results')
  protected profilerEnable(): boolean {
    THREE.ProfilerService.enable();
    return true;
  }

  @ConsoleCommand('profiler.disable', 'Disable CPU profiling')
  protected profilerDisable(): boolean {
    THREE.ProfilerService.disable();
    return true;
  }

  @ConsoleCommand('profiler.report', 'Print CPU timing table sorted by average cost')
  protected profilerReport(): boolean {
    THREE.ProfilerService.report();
    return true;
  }

  @ConsoleCommand('profiler.reset', 'Clear all accumulated profiling samples')
  protected profilerReset(): boolean {
    THREE.ProfilerService.reset();
    return true;
  }

  @ConsoleCommand('loadworld', 'Load world from file path (usage: loadworld <path>)')
  protected loadWorld(path: string): boolean {
    this.loadLevelDataFromPathInternal(AssetPath.fromString(path));
    return true;
  }



  @ConsoleCommand('reload', 'Reload the current world')
  protected reload(): boolean {
    if (!this.world) return false;
    const worldData = this.world.asExportedObject();
    const worldDataString = JSON.stringify(worldData);
    this.world.clearWorld();
    WorldSerializer.loadWorld(this.world, parseJson(worldDataString)).then(() => {
      // this is a hack as currently there's no way to restore the camera
      const controller = this.world?.getFirstActor(PlayerController);
      const pawn = this.world?.getFirstActor(Pawn);
      if (controller && pawn) {
        controller.possess(pawn);
      }

      console.dir(worldData);
    });

    return true;
  }

  protected onConnectionStarted(): void {
    // Create connection status overlay (only if world exists)
    if (this._world) {
      const statusOverlay = new ConnectionStatusOverlay(this.container);
      statusOverlay.show();
      statusOverlay.setStatus('connecting');
      this._world.connectionStatusOverlay = statusOverlay;
    }
  }

  protected onConnectionEstablished(clientId: string): void {
    if (this._world?.connectionStatusOverlay) {
      this._world.connectionStatusOverlay.setStatus('connected', clientId);
    }
  }

  protected onConnectionFailed(_error: Error): void {
    // Update connection status overlay
    if (this._world?.connectionStatusOverlay) {
      this._world.connectionStatusOverlay.setStatus('disconnected');
    }
  }

  protected onConnectionClosed(): void {
    // Update connection status overlay
    if (this._world?.connectionStatusOverlay) {
      this._world.connectionStatusOverlay.hide();
    }
  }

  public async connectToServer(serverUrl: string): Promise<void> {
    if (!this.netWorld) {
      throw new Error('[GameLoop] Cannot connect to server: netWorld not initialized');
    }

    // Check if already connected to avoid duplicate connection attempts.
    if (this.netWorld instanceof ClientNetWorld && this.netWorld.getGameSession() !== null) {
      console.log('[GameLoop] Already connected to server, skipping connection');
      return;
    }

    // Capture the standalone clientId before swapping netWorld — ClientNetWorld gets a new
    // clientId in its constructor, so reading it after the swap would miss the standalone actors.
    const standaloneClientId = this.netWorld.clientId;

    // Swap StandaloneNetWorld → ClientNetWorld for the duration of this server connection.
    if (this.netWorld instanceof StandaloneNetWorld) {
      console.log('[GameLoop] Upgrading StandaloneNetWorld → ClientNetWorld');
      const clientNetWorld = new ClientNetWorld(this.options.gameId ?? 'default');
      this.netWorld = clientNetWorld;
      if (this.world) {
        (this.world as any).netWorld = clientNetWorld;
        clientNetWorld.setWorld(this.world);
      }
    }

    this.onConnectionStarted();

    try {
      // Clear the game mode and any local player actors when connecting to server
      // The server provides game state, so local game mode should not be active
      if (this.world && this.world.gameMode) {
        console.log('[GameLoop] Clearing game mode and local player actors - server will provide game state');

        // Destroy any local player controller and pawn that were spawned in standalone mode.
        // Use standaloneClientId (captured before the netWorld swap) — the new ClientNetWorld
        // has a different clientId and would not match the standalone actors.
        const localActors = this.world.getActors(PlayerController);
        for (const actor of localActors) {
          if (actor.netOwningClientId === standaloneClientId) {
            console.log('[GameLoop] Destroying local player controller spawned in standalone mode');
            this.world.removeActors(actor);
          }
        }

        // Only call endPlay if the game mode has been started
        if (this.world.gameMode.getPlayState() === PlayState.Playing) {
          this.world.gameMode.endPlay();
        }
        (this.world as any).gameMode = null;
      }

      console.log(`[GameLoop] Connecting to server: ${serverUrl}`);
      if (!(this.netWorld instanceof ClientNetWorld)) {
        throw new Error('[GameLoop] connectToServer requires a ClientNetWorld instance');
      }
      await this.netWorld.connectToServer({
        serverUrl,
        lagSimulationConfig: this.gameContext.getNetSettings().clientLagSimulationConfig
      });

      NetRuntime.setType(NetRuntimeType.Client);
      const clientId = this.netWorld.getGameSession()?.getAssignedClientId();
      console.log(`[GameLoop] ✓ Connected to server with clientId: ${clientId}`);
      this.onConnectionEstablished(clientId ?? '');
    } catch (error) {
      console.error('[GameLoop] ✗ Failed to connect to server:', error);
      this.onConnectionFailed(error as Error);
      throw error;
    }
  }

  public async disconnectFromServer(): Promise<void> {
    if (!this.isConnectedToServer()) {
      console.log('[GameLoop] Not connected to server');
      return;
    }

    try {
      console.log('[GameLoop] Disconnecting from server...');

      // Phase 2: Clean up network connection and session BEFORE removing actors
      // This prevents RPC errors when actors are destroyed
      if (this.netWorld) {
        this.netWorld.getGameSession()?.destroy();
        this.netWorld.clearNetObjects();
      }

      // Phase 3: Switch runtime type and install a fresh StandaloneNetWorld BEFORE the
      // world transition so that getWorldConfiguration() returns the correct netWorld
      // when GameContext creates the new world.
      NetRuntime.setType(NetRuntimeType.Standalone);
      this.netWorld = new StandaloneNetWorld(this.options.gameId ?? 'default');
      console.log('[GameLoop] ✓ Disconnected — transitioning to standalone world');

      // Phase 4: Full world transition — destroys the old world and creates a fresh one
      // with the scene's GameMode and the new StandaloneNetWorld.  Actors are loaded but
      // beginPlay has NOT been called yet.
      await this.gameContext.reloadInitialScene();

      // Phase 5: Post-creation subsystem setup on the new world (mirrors start()).
      // preStart() is intentionally skipped — it is for one-time game setup, not reloads.
      this.setupWorldAfterCreation();
      await resourceManager.waitForResources(ResourceType.All);
      await this.loadSceneResources(this.world);
      await this.world.createNavigationServer();
      await this.world.createManifold();
      await this.loadSceneResources(this.world);

      // Phase 6: Begin play — StandaloneNetWorld.doBeginPlay() fires naturally, which
      // calls GameMode.onPlayerJoined() to spawn the local PlayerController and Pawn.
      this.world.beginPlay();

      // Notify subclasses of disconnection
      this.onConnectionClosed();
    } catch (error) {
      console.error('[GameLoop] Error during disconnect:', error);
      throw error;
    }
  }

  @ConsoleCommand('toggleconnectionstatus', 'Toggle connection status overlay')
  protected toggleConnectionStatus(): boolean {
    if (this.world?.connectionStatusOverlay) {
      this.world.connectionStatusOverlay.toggle();
      return true;
    }
    return false;
  }

  protected isConnectedToServer(): boolean {
    return this.world.netWorld?.getGameSession() !== null;
  }
}
